import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { SettingsCard } from "../SettingsCard";
import { AddStaffModal } from "./AddStaffModal";
import { AccessControlModal } from "./AccessControlModal";
import { RoleCatalogCard } from "./roles/RoleCatalogCard";
import { SettingsAPI, StaffMember } from "../../../services/settings.service";
import { avatarColor, initials } from "./accessHelpers";

// ── Main section ──────────────────────────────────────────────────────────────

export const AccessPersonalizations: React.FC = () => {
    const [staff, setStaff] = useState<StaffMember[]>([]);
    const [loading, setLoading] = useState(true);

    // Access Control card — full-table modal ("View All") and the add-member modal.
    const [showAccessModal, setShowAccessModal] = useState(false);
    const [showAddStaff, setShowAddStaff] = useState(false);

    useEffect(() => {
        SettingsAPI.getStaff()
            .then(setStaff)
            .catch(() => toast.error("Failed to load staff"))
            .finally(() => setLoading(false));
    }, []);

    // AddStaffModal calls this after POST /clinic/staff succeeds.
    const handleStaffAdded = (member: StaffMember) => {
        setStaff(prev => [...prev, member]);
    };

    if (loading) {
        return <div className="text-sm text-gray-400 py-10 text-center">Loading…</div>;
    }

    const preview = staff.slice(0, 3);

    return (
        <div className="space-y-5">

            {/* ── Card 1: Access Control & Permissions (real staff, role-based access) ── */}
            <SettingsCard
                title="Access Control & Permissions"
                subtitle="Manage who can access patient records and system features"
                icon={
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                }
                // The modal is the only place to manage roles, revoke or reactivate,
                // so it must be reachable at any team size - gating it on >3 staff
                // left small clinics with no way to remove anyone.
                headerRight={
                    staff.length > 0 ? (
                        <button
                            onClick={() => setShowAccessModal(true)}
                            className="text-xs font-medium text-blue-600 hover:text-blue-700"
                        >
                            {staff.length > 3 ? "View All" : "Manage"}
                        </button>
                    ) : undefined
                }
                // Always-visible "Add Member" at the card's bottom-right. Previously the
                // only entry point was inside the "View All" modal, which is hidden when
                // the clinic has ≤3 staff — leaving no way to add a member in a small clinic.
                footer={
                    <div className="flex justify-end">
                        <button
                            onClick={() => setShowAddStaff(true)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3.5 py-2 rounded-lg transition-colors"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                <circle cx="9" cy="7" r="4" />
                                <path d="M19 8v6M22 11h-6" />
                            </svg>
                            Add Member
                        </button>
                    </div>
                }
            >
                {staff.length === 0 ? (
                    <p className="text-sm text-gray-400 py-4">No staff members found.</p>
                ) : (
                    <div className="space-y-3">
                        {preview.map((m, i) => {
                            const name   = `${m.first_name} ${m.last_name}`.trim() || m.email;
                            return (
                                <div key={m.user_id}
                                    className="flex items-center gap-3 border border-gray-100 rounded-xl px-4 py-3">
                                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColor(i)} flex items-center justify-center text-white text-sm font-semibold flex-shrink-0`}>
                                        {initials(m.first_name, m.last_name, m.email)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                                        <p className="text-xs text-gray-500 truncate">{m.designation || m.role}</p>
                                    </div>
                                    <div className="hidden sm:flex items-center gap-1.5 w-20 flex-shrink-0">
                                        <span className={`w-2 h-2 rounded-full ${m.is_active ? "bg-green-500" : "bg-gray-300"}`} />
                                        <span className={`text-xs ${m.is_active ? "text-green-600" : "text-gray-400"}`}>
                                            {m.is_active ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </SettingsCard>

            {/* ── Card 2: Role Catalog — per your instruction, a card in this
                section rather than a new top-level Settings nav entry. ── */}
            <RoleCatalogCard />

            {/* Privacy Settings card intentionally hidden for now — placeholder
                with no backend wiring (no use case yet). Restore from git history. */}

            {/* Full team-access table (opened from "View All") */}
            <AccessControlModal
                isOpen={showAccessModal}
                onClose={() => setShowAccessModal(false)}
                staff={staff}
                onAddMember={() => setShowAddStaff(true)}
                onMemberUpdate={updated =>
                    setStaff(prev => prev.map(m => m.user_id === updated.user_id ? updated : m))
                }
            />

            {/* Add-member form (opened from inside the access modal) */}
            <AddStaffModal
                isOpen={showAddStaff}
                onClose={() => setShowAddStaff(false)}
                onSuccess={handleStaffAdded}
            />

        </div>
    );
};
